import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import nextActionOptionService from '../services/nextActionOptionService';
import { NextActionOption } from '../types/cpe';

const SettingsPage: React.FC = () => {
  const { t } = useTranslation();

  const [options, setOptions] = useState<NextActionOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add / edit state
  const [addingNew, setAddingNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newNameError, setNewNameError] = useState('');
  const [savingNew, setSavingNew] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editNameError, setEditNameError] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<NextActionOption | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadOptions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await nextActionOptionService.getAll();
      setOptions(data);
    } catch {
      setError(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  const handleAddSubmit = async () => {
    const trimmed = newName.trim();
    if (!trimmed) {
      setNewNameError(t('validation.required', { field: t('settings.nextActionOptions.name') }));
      return;
    }
    setSavingNew(true);
    try {
      await nextActionOptionService.create(trimmed);
      toast.success(t('settings.nextActionOptions.createSuccess'));
      setAddingNew(false);
      setNewName('');
      await loadOptions();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || t('errors.generic');
      setNewNameError(msg);
    } finally {
      setSavingNew(false);
    }
  };

  const startEdit = (option: NextActionOption) => {
    setEditingId(option.id);
    setEditName(option.name);
    setEditNameError('');
  };

  const handleEditSubmit = async (id: string) => {
    const trimmed = editName.trim();
    if (!trimmed) {
      setEditNameError(t('validation.required', { field: t('settings.nextActionOptions.name') }));
      return;
    }
    setSavingEdit(true);
    try {
      await nextActionOptionService.update(id, { name: trimmed });
      toast.success(t('settings.nextActionOptions.updateSuccess'));
      setEditingId(null);
      await loadOptions();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || t('errors.generic');
      setEditNameError(msg);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleToggleActive = async (option: NextActionOption) => {
    try {
      await nextActionOptionService.update(option.id, { is_active: !option.is_active });
      toast.success(
        option.is_active
          ? t('settings.nextActionOptions.archiveSuccess')
          : t('settings.nextActionOptions.unarchiveSuccess')
      );
      await loadOptions();
    } catch {
      toast.error(t('errors.generic'));
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await nextActionOptionService.delete(deleteTarget.id);
      toast.success(t('settings.nextActionOptions.deleteSuccess'));
      setDeleteTarget(null);
      await loadOptions();
    } catch {
      toast.error(t('errors.generic'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 3 }}>
          {t('settings.title')}
        </Typography>

        {/* Next Action Options Section */}
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t('settings.nextActionOptions.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t('settings.nextActionOptions.description')}
              </Typography>
            </Box>
            {!addingNew && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => { setAddingNew(true); setNewName(''); setNewNameError(''); }}
              >
                {t('settings.nextActionOptions.add')}
              </Button>
            )}
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {/* Add new row */}
          {addingNew && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
                p: 1.5,
                border: '1px dashed',
                borderColor: 'primary.main',
                borderRadius: 1,
              }}
            >
              <TextField
                autoFocus
                size="small"
                placeholder={t('settings.nextActionOptions.namePlaceholder')}
                label={t('settings.nextActionOptions.name')}
                value={newName}
                onChange={(e) => { setNewName(e.target.value); setNewNameError(''); }}
                error={!!newNameError}
                helperText={newNameError}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddSubmit(); if (e.key === 'Escape') setAddingNew(false); }}
                sx={{ flex: 1 }}
              />
              <Tooltip title={t('common.save', 'Save')}>
                <span>
                  <IconButton
                    color="primary"
                    onClick={handleAddSubmit}
                    disabled={savingNew}
                    size="small"
                  >
                    {savingNew ? <CircularProgress size={18} /> : <CheckIcon />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('common.cancel', 'Cancel')}>
                <IconButton size="small" onClick={() => setAddingNew(false)}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : options.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              {t('settings.nextActionOptions.noOptions')}
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      {t('settings.nextActionOptions.name')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: 120 }}>
                      {t('common.status', 'Status')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', width: 160 }}>
                      {t('common.actions', 'Actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {options.map((option) => (
                    <TableRow key={option.id} hover>
                      <TableCell>
                        {editingId === option.id ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                              autoFocus
                              size="small"
                              value={editName}
                              onChange={(e) => { setEditName(e.target.value); setEditNameError(''); }}
                              error={!!editNameError}
                              helperText={editNameError}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleEditSubmit(option.id);
                                if (e.key === 'Escape') setEditingId(null);
                              }}
                              sx={{ flex: 1 }}
                            />
                            <Tooltip title={t('common.save', 'Save')}>
                              <span>
                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => handleEditSubmit(option.id)}
                                  disabled={savingEdit}
                                >
                                  {savingEdit ? <CircularProgress size={16} /> : <CheckIcon />}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={t('common.cancel', 'Cancel')}>
                              <IconButton size="small" onClick={() => setEditingId(null)}>
                                <CloseIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{ color: option.is_active ? 'text.primary' : 'text.disabled' }}
                          >
                            {option.name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={
                            option.is_active
                              ? t('settings.nextActionOptions.active')
                              : t('settings.nextActionOptions.archived')
                          }
                          color={option.is_active ? 'success' : 'default'}
                          variant={option.is_active ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {editingId !== option.id && (
                          <>
                            <Tooltip title={t('settings.nextActionOptions.edit')}>
                              <IconButton size="small" onClick={() => startEdit(option)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title={
                                option.is_active
                                  ? t('settings.nextActionOptions.archive')
                                  : t('settings.nextActionOptions.unarchive')
                              }
                            >
                              <IconButton size="small" onClick={() => handleToggleActive(option)}>
                                {option.is_active ? (
                                  <ArchiveIcon fontSize="small" />
                                ) : (
                                  <UnarchiveIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('settings.nextActionOptions.delete')}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteTarget(option)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>{t('settings.nextActionOptions.delete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('settings.nextActionOptions.confirmDelete')}
          </DialogContentText>
          {deleteTarget && (
            <Typography sx={{ mt: 1, fontWeight: 'bold' }}>
              &ldquo;{deleteTarget.name}&rdquo;
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={18} /> : t('settings.nextActionOptions.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SettingsPage;
